import type { ReactNode } from 'react';

import type {
  AdminSystemStatusRegistrySwitches,
  AdminSystemStatusSnapshot,
  SandboxGcSweepCounters,
  VecGcSweepCounters,
} from '@/versions/v1/shared/admin-seed';
import { ADMIN_SEED_ANCHOR_MS } from '@/versions/v1/shared/admin-seed';

export function formatRelativeMs(
  epochMs: number | null,
  now = ADMIN_SEED_ANCHOR_MS,
): string {
  if (epochMs === null) return 'None since process start';
  const delta = now - epochMs;
  if (delta < 60_000) return 'Just now';
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
  return `${Math.floor(delta / 86_400_000)}d ago`;
}

export function formatTimestamp(epochMs: number | null): string {
  if (epochMs === null) return 'Not run yet';
  return new Date(epochMs).toLocaleString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function formatUptime(ms: number): string {
  const sec = Math.floor(ms / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (day > 0) return `${day}d ${hr % 24}h`;
  if (hr > 0) return `${hr}h ${min % 60}m`;
  if (min > 0) return `${min}m ${sec % 60}s`;
  return `${sec}s`;
}

export function allowlistLabel(
  value: AdminSystemStatusRegistrySwitches['allowedConnectors'],
): string {
  if (value === 'default') return 'Default / env-only';
  if (value.allowAll) return 'Allow all';
  return `${value.count} selected`;
}

export type MetricRowProps = { label: string; value: ReactNode };

export function mockVecGcSweep(): VecGcSweepCounters {
  return {
    scanned: 12840,
    orphans: 12,
    purged: 12,
    durationMs: 842,
  };
}

export function mockSandboxGcSweep(): SandboxGcSweepCounters {
  return {
    hibernated: 3,
    destroyed: 1,
    errors: 0,
    durationMs: 1204,
  };
}

export function touchGcSnapshot(
  snapshot: AdminSystemStatusSnapshot,
): AdminSystemStatusSnapshot {
  return {
    ...snapshot,
    gc: {
      vecGc: {
        ...snapshot.gc.vecGc,
        lastRunAt: ADMIN_SEED_ANCHOR_MS,
        runsLastNDays: snapshot.gc.vecGc.runsLastNDays + 1,
      },
      sandboxSweeper: snapshot.gc.sandboxSweeper,
    },
  };
}

export function touchSandboxSnapshot(
  snapshot: AdminSystemStatusSnapshot,
): AdminSystemStatusSnapshot {
  return {
    ...snapshot,
    gc: {
      vecGc: snapshot.gc.vecGc,
      sandboxSweeper: {
        ...snapshot.gc.sandboxSweeper,
        lastRunAt: ADMIN_SEED_ANCHOR_MS,
        runsLastNDays: snapshot.gc.sandboxSweeper.runsLastNDays + 1,
        hibernatedLastNDays: snapshot.gc.sandboxSweeper.hibernatedLastNDays + 3,
        destroyedLastNDays: snapshot.gc.sandboxSweeper.destroyedLastNDays + 1,
      },
    },
  };
}
